/**
 * The catalogue behind "Add to your trip", and the sentence it hands Steward.
 *
 * Pure and separate from the sheet so the load-bearing part — what a tapped tile
 * actually ASKS — is assertable without a DOM. The seed sentence is the whole
 * contract between a tap and a good answer: "Find me somewhere to stay" gets a
 * generic reply, "Find me somewhere to stay in Anchorage for Thu Sep 11, I fly
 * home Sep 13" gets a travel agent's reply.
 */
import type { ItineraryItemType, StewardCategory, StewardScope } from "@/lib/types";

/** One tile in the picker. */
export interface AddChoice {
  /** Stable id — also the analytics/test handle. */
  key: string;
  label: string;
  /** One-line "what this is for", shown under the label on wide screens. */
  hint: string;
  /** What Steward would shop for. Null = nothing to research (a plain note). */
  category: StewardCategory | null;
  /** The itinerary row it becomes if the traveler enters it by hand. */
  itemType: ItineraryItemType;
  /** Lucide icon name — resolved by the sheet, kept out of this pure module. */
  icon:
    | "Hotel"
    | "Home"
    | "Utensils"
    | "Ticket"
    | "Landmark"
    | "Compass"
    | "Car"
    | "TrainFront"
    | "Bus"
    | "Star"
    | "StickyNote";
}

/**
 * Ordered by how often a traveler reaches for it, not alphabetically. Stay and
 * eat lead because they are most of every trip; the note is last because it is
 * the only one that isn't really planning.
 */
export const ADD_CHOICES: AddChoice[] = [
  {
    key: "hotel",
    label: "Hotel",
    hint: "Rooms, rates, photos",
    category: "hotel",
    itemType: "lodging",
    icon: "Hotel",
  },
  {
    key: "rental",
    label: "Vacation rental",
    hint: "Apartments and houses",
    category: "rental",
    itemType: "lodging",
    icon: "Home",
  },
  {
    key: "dining",
    label: "Restaurant",
    hint: "Dinner, lunch, coffee",
    category: "dining",
    itemType: "dining",
    icon: "Utensils",
  },
  {
    key: "car",
    label: "Rental car",
    hint: "Pick-up to drop-off",
    category: "car",
    itemType: "transfer",
    icon: "Car",
  },
  {
    key: "transfer",
    label: "Transfer",
    hint: "Shuttle, taxi, ferry",
    category: "transfer",
    itemType: "transfer",
    icon: "Bus",
  },
  {
    key: "train",
    label: "Train",
    hint: "Rail between cities",
    category: "train",
    itemType: "transfer",
    icon: "TrainFront",
  },
  {
    key: "tour",
    label: "Tour",
    hint: "Guided trips and excursions",
    category: "tour",
    itemType: "activity",
    icon: "Compass",
  },
  {
    key: "attraction",
    label: "Attraction",
    hint: "Museums, parks, landmarks",
    category: "attraction",
    itemType: "activity",
    icon: "Landmark",
  },
  {
    key: "activity",
    label: "Something to do",
    hint: "Anything for an open day",
    category: "activity",
    itemType: "activity",
    icon: "Ticket",
  },
  {
    key: "event",
    label: "Event",
    hint: "A show, game or festival",
    category: "event",
    itemType: "event",
    icon: "Star",
  },
  {
    key: "note",
    label: "Note",
    hint: "Just a reminder to yourself",
    category: null,
    itemType: "note",
    icon: "StickyNote",
  },
];

export function findChoice(key: string | null | undefined): AddChoice | null {
  if (!key) return null;
  return ADD_CHOICES.find((c) => c.key === key) ?? null;
}

/** Where in the trip the add slot sits. */
export interface AddContext {
  /** YYYY-MM-DD, when the slot is tied to a day. */
  date?: string | null;
  city?: string | null;
  /** Human phrase: "before your flight out of SEA", "after you land at ANC". */
  position?: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-09-11" → "Thu Sep 11". Returns null for anything that isn't a date. */
export function formatSlotDay(ymd: string | null | undefined): string | null {
  const m = typeof ymd === "string" ? ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  return `${DAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** "Thu Sep 11 · Anchorage · after you land" — the sheet's context subline. */
export function contextSubline(ctx: AddContext): string | null {
  const bits = [formatSlotDay(ctx.date), ctx.city?.trim() || null, ctx.position?.trim() || null]
    .filter(Boolean);
  return bits.length > 0 ? bits.join(" · ") : null;
}

/**
 * How the traveler would phrase the request themselves. This is what gets sent
 * as the first message, so it appears in the thread verbatim — it has to read
 * like a person, not like a form submission.
 */
const ASK: Record<string, string> = {
  hotel: "Find me somewhere to stay",
  rental: "Find me a vacation rental",
  dining: "Where should we eat",
  car: "I need a rental car",
  transfer: "How should we get around",
  train: "Look at trains for me",
  tour: "Find us a tour",
  attraction: "What's worth seeing",
  activity: "What should we do",
  event: "What's on",
};

/**
 * Build the message a tapped tile sends. Never returns an empty string — a
 * category with no phrasing falls back to its label, because a blank first
 * message would open the workspace to silence.
 */
export function buildSeedMessage(choice: AddChoice, ctx: AddContext): string {
  const base = ASK[choice.key] ?? `Help me add a ${choice.label.toLowerCase()}`;
  const where = ctx.city?.trim() ? ` in ${ctx.city.trim()}` : "";
  const day = formatSlotDay(ctx.date);
  const when = day ? ` for ${day}` : "";
  const position = ctx.position?.trim() ? ` — this is ${ctx.position.trim()}` : "";
  return `${base}${where}${when}${position}.`;
}

/** The structured twin of the seed message (see backend TurnScope). */
export function buildScope(choice: AddChoice, ctx: AddContext): StewardScope {
  return {
    date: ctx.date ?? null,
    city: ctx.city ?? null,
    category: choice.category,
    position: ctx.position ?? null,
  };
}

/**
 * A free-typed request still deserves the slot's context. The traveler typed
 * the words; we only attach where and when they were standing.
 */
export function buildFreeScope(ctx: AddContext): StewardScope {
  return {
    date: ctx.date ?? null,
    city: ctx.city ?? null,
    category: null,
    position: ctx.position ?? null,
  };
}
